import { Button } from "../../components/core/basic/Button";
import Loading from "../../components/core/blocks/Loading";
import SummaryCard from "../../components/core/blocks/SummaryCard";
import { ProgramContextProvider, useProgramContext } from "../../components/custom/programs/ProgramContext";
import ProgramsForm from "../../components/custom/programs/ProgramsForm";
import { useFetch } from "../../hooks/api";
import { Col, Container as Grid, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import api from "../../utils/api";
import { errorToast } from "../../utils/toasts";
import { toTitleCase } from "../../utils/formatText";
import { ENGINES } from "../../modules/programs/constants";
import { useSimpleCan } from "../../config/casl/casl";
import { useModuleEnabled } from "../../redux/selectors/modules";

const ProgramEdit = ({ id, defaultValues }) => {
  const { t } = useTranslation(["programs", "common"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const { engine } = useProgramContext();

  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");

  const isReadOnly =
    (["bidding", "budget"].includes(engine) && !userCanUpdateAds) ||
    (engine === "lineitem_bidding" && !userCanUpdateDSPEntity);

  const { handleSubmit, ...form } = useForm({
    shouldUnregister: false,
    defaultValues,
    mode: "onChange",
  });

  const onSuccess = () => {
    queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "programs" });
    ["bidding", "budget"].includes(engine) ? router.history.back() : navigate({ to: "/user/programs" });
  };

  const mutation = useMutation({
    mutationFn: async (data) => {
      const { name, start_date, end_date, ...rawSpec } = data;
      await api.patch(`/programs/${id}/`, { id, name, start_date, end_date, spec: rawSpec, status: "scheduled" });
    },
    onSuccess,
  });

  const isPending = mutation.isPending || form.formState.isSubmitting;
  const engineTitleSelector = ENGINES?.[engine]?.title;
  const title =
    engineTitleSelector ? t(engineTitleSelector) : t(($) => $.programs["{name} Rules"], { name: toTitleCase(engine) });

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <Grid fluid style={{ padding: "3rem", minHeight: "calc(100vh - 150px)" }}>
        <SummaryCard title={t(($) => $.programs["Edit {title}"], { title })}>
          <Row>
            <Col xs={12} lg={8} className="m-auto">
              <ProgramsForm {...form} isReadOnly={isReadOnly} />
              <Row className="mt-4">
                <Col md={12} className="d-flex justify-content-end">
                  <div style={{ marginRight: "2rem" }}>
                    <Button
                      variant="secondary"
                      onPress={() => {
                        router.history.back();
                      }}
                    >
                      {t(($) => $.common.actions.Cancel)}
                    </Button>
                  </div>
                  {!isReadOnly && (
                    <div>
                      <Button
                        variant="primary"
                        isPending={isPending}
                        onPress={() => {
                          return handleSubmit(mutation.mutate, () =>
                            errorToast({ message: t(($) => $.programs["Some fields are missing or invalid."]) }),
                          )();
                        }}
                      >
                        {t(($) => $.programs.footer.Update)}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </SummaryCard>
      </Grid>
    </FormProvider>
  );
};

const Wrapper = () => {
  const { id: programId } = useParams({ strict: false });
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");

  const { data, loading } = useFetch(
    ["programs", programId],
    `/programs/${programId}/`,
    { all_user_orgs: hasMultiOrgRulesModuleEnabled },
    {
      select: (data) => data.data,
      staleTime: Infinity,
      refetchOnMount: "always",
    },
  );
  if (data && !loading) {
    const { id, name, start_date, end_date, spec, engine } = data;

    return (
      <ProgramContextProvider engine={engine}>
        <ProgramEdit
          id={id}
          defaultValues={{
            name,
            start_date,
            end_date,
            ...spec,
          }}
        />
      </ProgramContextProvider>
    );
  }
  return <Loading />;
};

export default Wrapper;
